/**
 * @file cabinet-header-placement.ts
 * @description Раскладка кнопок заголовка страницы по местам (phase19.31,
 * приёмка QA-5 D3/D13): что стоит в верхней панели, а что — строкой под
 * шапкой. Чистая функция — правило проверяется без каркаса.
 *
 * Название страницы важнее кнопок: в панели название ужимается, кнопки нет.
 * Поэтому число кнопок в панели ограничено шириной:
 *  - сжатый заголовок (< 1024 у магазина) — в панели ничего, все готовые
 *    кнопки строкой под шапкой, главное действие — в нижней полосе;
 *  - 1024–1279 — в панели только главное действие, `panelActions` и
 *    `secondaryActions` строкой под шапкой;
 *  - от 1280 — в панели главное действие и `panelActions` (не больше двух
 *    кнопок), `secondaryActions` — в панели, если не просили `secondaryBelow`.
 *
 * Здесь же — свойства `CabinetPageHeader` (компонент держим ≤ 200 строк).
 */

import type { ReactNode } from "react";

import type { PageAction } from "./page-actions";

/** Свойства заголовка страницы кабинета. */
export interface CabinetPageHeaderProps {
  title: string;
  /** Пояснение одной фразой. */
  subtitle?: string;
  /** Статус-пилюля рядом с названием (документ, задание). */
  status?: ReactNode;
  /** Кнопка «назад» слева от названия. */
  back?: { to: string; label: string };
  /** Главное действие страницы — одна кнопка (обычно `variant="brand"`), SC34. */
  action?: ReactNode;
  /** Второстепенные действия данными — не больше двух; на узком экране — в «⋯». */
  secondary?: PageAction[];
  /** Действия только в «⋯». */
  menu?: PageAction[];
  /** Подпись «⋯» для скринридера. */
  menuLabel?: string;
  /** Что положить в нижнюю полосу вместо `action` (страница без главного действия). */
  mobileAction?: ReactNode;
  /** Подпись над кнопкой нижней полосы: «Обновлено в 14:32». */
  mobileNote?: ReactNode;
  /**
   * @deprecated Готовые кнопки рядом с главным действием (кабинет платформы до
   * О4). На телефоне уходят первой строкой содержимого. Новые страницы — `secondary`.
   */
  secondaryActions?: ReactNode;
  /**
   * `secondaryActions` строкой под шапкой и на широком экране; в панели —
   * только главное действие. Для длинных подписей кнопок, которые в панели
   * съедали название до «Новый …» (приёмка qa4 п. 3). По умолчанию — в панели.
   */
  secondaryBelow?: boolean;
  /**
   * Кнопки рядом с главным действием в панели — только от 1280 (не больше
   * двух); уже — первыми в строке под шапкой. Название важнее кнопок (QA-5 D3).
   */
  panelActions?: ReactNode;
}

/** Где стоят готовые кнопки заголовка. */
export interface HeaderPlacement {
  /** `panelActions` в панели. */
  panelActionsInPanel: boolean;
  /** `secondaryActions` в панели. */
  secondaryInPanel: boolean;
  /** `panelActions` строкой под шапкой. */
  panelActionsInRow: boolean;
  /** `secondaryActions` строкой под шапкой. */
  secondaryInRow: boolean;
}

export interface HeaderPlacementInput {
  /** Сжатый заголовок (телефон, планшет). */
  compact: boolean;
  /** Ширина от 1280. */
  wide: boolean;
  /** Страница попросила `secondaryActions` строкой под шапкой. */
  secondaryBelow: boolean;
  hasPanelActions: boolean;
  hasSecondaryActions: boolean;
}

export function headerPlacement(input: HeaderPlacementInput): HeaderPlacement {
  const { compact, wide, secondaryBelow, hasPanelActions, hasSecondaryActions } = input;
  const panelActionsInPanel = hasPanelActions && !compact && wide;
  const secondaryInPanel = hasSecondaryActions && !compact && !secondaryBelow;
  return {
    panelActionsInPanel,
    secondaryInPanel,
    panelActionsInRow: hasPanelActions && !panelActionsInPanel,
    secondaryInRow: hasSecondaryActions && !secondaryInPanel,
  };
}
